import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminPages.css';

const emptyForm = { code: '', discountType: 'percentage', discountValue: '', minOrder: '', maxUses: '', expiresAt: '' };

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchCoupons(); }, []);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get('/api/admin/coupons');
      setCoupons(res.data);
    } catch (err) {
      console.error('Error fetching coupons:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAddForm = () => { setForm(emptyForm); setEditingId(null); setError(''); setShowForm(true); };

  const openEditForm = (c) => {
    setForm({
      code: c.code, discountType: c.discountType, discountValue: c.discountValue.toString(),
      minOrder: c.minOrderAmount?.toString() || '', maxUses: c.usageLimit?.toString() || '',
      expiresAt: c.validUntil ? new Date(c.validUntil).toISOString().split('T')[0] : ''
    });
    setEditingId(c._id); setError(''); setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditingId(null); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.discountValue) { setError('Code and discount value are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, discountValue: parseFloat(form.discountValue), minOrder: form.minOrder ? parseFloat(form.minOrder) : 0, maxUses: form.maxUses ? parseInt(form.maxUses) : 0 };
      if (editingId) {
        await axios.put(`/api/admin/coupons/${editingId}`, payload);
      } else {
        await axios.post('/api/admin/coupons', payload);
      }
      closeForm(); fetchCoupons();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving coupon');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try { await axios.delete(`/api/admin/coupons/${id}`); fetchCoupons(); } catch (err) { alert('Error deleting coupon'); }
  };

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="admin-page admin-page-wide">
      <div className="admin-page-header">
        <div>
          <h2 className="admin-page-title">Coupons</h2>
          <p className="admin-page-desc">Manage discount coupons and promotions. ({coupons.length} total)</p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={openAddForm}>+ Add Coupon</button>
      </div>

      {showForm && (
        <div className="admin-modal-overlay" onClick={closeForm}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editingId ? 'Edit Coupon' : 'Add New Coupon'}</h3>
              <button className="admin-modal-close" onClick={closeForm}>&times;</button>
            </div>
            <form onSubmit={handleSubmit} className="admin-material-form">
              {error && <div className="admin-form-error">{error}</div>}
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label>Coupon Code *</label>
                  <input type="text" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. SAVE20" required />
                </div>
                <div className="admin-form-group">
                  <label>Discount Type</label>
                  <select value={form.discountType} onChange={e => setForm({ ...form, discountType: e.target.value })}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Discount Value *</label>
                  <input type="number" value={form.discountValue} onChange={e => setForm({ ...form, discountValue: e.target.value })} placeholder={form.discountType === 'percentage' ? 'e.g. 20' : 'e.g. 500'} required min="0" />
                </div>
                <div className="admin-form-group">
                  <label>Min Order Value</label>
                  <input type="number" value={form.minOrder} onChange={e => setForm({ ...form, minOrder: e.target.value })} placeholder="e.g. 1000" min="0" />
                </div>
                <div className="admin-form-group">
                  <label>Max Uses (0 = unlimited)</label>
                  <input type="number" value={form.maxUses} onChange={e => setForm({ ...form, maxUses: e.target.value })} placeholder="e.g. 100" min="0" />
                </div>
                <div className="admin-form-group">
                  <label>Expires At</label>
                  <input type="date" value={form.expiresAt} onChange={e => setForm({ ...form, expiresAt: e.target.value })} />
                </div>
              </div>
              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>{saving ? 'Saving...' : editingId ? 'Update' : 'Add Coupon'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-page-card admin-table-card">
        <div className="admin-notifications-table">
          <table>
            <thead>
              <tr><th>Code</th><th>Discount</th><th>Min Order</th><th>Uses</th><th>Expires</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {coupons.length === 0 ? (
                <tr><td colSpan="7" className="admin-no-data">No coupons yet.</td></tr>
              ) : coupons.map(c => {
                const isExpired = c.validUntil && new Date(c.validUntil) < new Date();
                return (
                  <tr key={c._id}>
                    <td><strong style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>{c.code}</strong></td>
                    <td>{c.discountType === 'percentage' ? `${c.discountValue}%` : `₹${c.discountValue}`}</td>
                    <td>{c.minOrderAmount ? `₹${c.minOrderAmount}` : '--'}</td>
                    <td>{c.usedCount ?? 0}{c.usageLimit ? ` / ${c.usageLimit}` : ''}</td>
                    <td>{c.validUntil ? new Date(c.validUntil).toLocaleDateString() : 'Never'}</td>
                    <td>
                      <span className={`admin-status-badge ${isExpired ? 'admin-status-pending' : 'admin-status-admin'}`}>
                        {isExpired ? 'Expired' : 'Active'}
                      </span>
                    </td>
                    <td>
                      <div className="admin-action-buttons">
                        <button className="admin-btn-icon admin-btn-edit" onClick={() => openEditForm(c)}>Edit</button>
                        <button className="admin-btn-icon admin-btn-delete" onClick={() => handleDelete(c._id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Coupons;
